import { createFileRoute } from "@tanstack/react-router";
import { alternatePath, SITE_URL } from "@/lib/i18n";
import { buildHead, breadcrumbJsonLd, faqJsonLd, serviceJsonLd } from "@/lib/seo";
import coverImg from "@/assets/hub-marketing-strategy-1600.webp";
import cover1200 from "@/assets/hub-marketing-strategy-1200.webp";
import cover800 from "@/assets/hub-marketing-strategy-800.webp";

const PATH = "/shopify-marketing-strategy";
const TITLE = "Shopify Marketing Strategy: 2026 Growth Playbook";
const DESCRIPTION =
  "A senior Shopify Plus operator's strategy framework: the 5 layers of a Shopify marketing strategy, a 90-day plan to grow your Shopify store, channel mix…";
const COVER_ABS = `${SITE_URL}/assets/hub-marketing-strategy-1600.webp`;
const COVER_SRCSET = `${cover800} 800w, ${cover1200} 1200w, ${coverImg} 1600w`;
const COVER_SIZES = "(min-width: 1024px) 1024px, 100vw";

const FAQS = [
  {
    q: "What is a Shopify marketing strategy?",
    a: "A Shopify marketing strategy is the system that connects acquisition, on-site conversion, retention, operations, and measurement around a single revenue model, designed for the specific constraints and advantages of the Shopify platform (theme, checkout, apps, Shop Pay, Shop Channel, Plus features). It is not a list of tactics; it is the order in which you fix and compound them.",
  },
  {
    q: "How do I grow my Shopify store organically?",
    a: "Fix the structural issues first: technical SEO (faceted navigation, indexable PLPs, 301 redirect hygiene), Core Web Vitals, and product-level information architecture. Then layer on content for high-intent informational and comparison queries, and use the Shop Channel + reviews schema to compound visibility. Most stores plateau because their crawl budget is spent on filtered URLs, not because they need more blog posts.",
  },
  {
    q: "How long does it take to see results from a Shopify marketing strategy?",
    a: "On a properly diagnosed store: paid and CRO changes show within 2–4 weeks, retention/email lifts within 30–60 days, and SEO compounds from month 3 onward. Replatform or technical SEO fixes (faceted nav, redirects) usually produce a step-change in 6–10 weeks once Google re-crawls.",
  },
  {
    q: "Shopify vs Shopify Plus, does the strategy change?",
    a: "Yes. Plus unlocks Shopify Functions, Checkout Extensibility, Scripts replacements, B2B, and Markets, which changes how you structure promotions, bundles, and international expansion. Below ~$2M GMV, standard Shopify with a well-architected theme is usually enough; above that, Plus features start to pay for themselves.",
  },
  {
    q: "What's the right marketing budget for a $1M–$10M Shopify store?",
    a: "A useful benchmark: 12–18% of net revenue across paid media, agency/in-house labor, tools, and content. The split shifts with maturity, early stage is paid-heavy, mature brands rebalance toward retention, brand, and SEO. The wrong question is 'how much should I spend?' The right one is 'what's my contribution margin after marketing, and where is the next dollar most efficient?'",
  },
  {
    q: "Do I need SEO if I'm already running paid ads?",
    a: "Yes. Paid is rented traffic; organic is owned. The healthiest Shopify stores we work with sit around 35–50% organic + direct, which lowers blended CAC and gives the business something to fall back on when ad costs spike. SEO also compounds, the page you publish today still works in 18 months.",
  },
  {
    q: "What KPIs should a Shopify marketing strategy track?",
    a: "Five numbers: contribution margin after marketing (not ROAS), blended CAC, 60-day repeat rate, organic + direct share of sessions, and conversion rate by landing-page template (PDP, PLP, collection). Everything else is a diagnostic in service of these.",
  },
  {
    q: "When should we hire an agency vs build in-house?",
    a: "In-house for brand, content cadence, customer service, and merchandising. Agency for technical SEO, performance engineering, replatforming, CRO experimentation systems, and analytics architecture, work that needs deep platform expertise but isn't a 40-hour-per-week job. Most $1M–$20M stores run a small in-house team plus a Plus-focused partner.",
  },
];

export const Route = createFileRoute("/shopify-marketing-strategy")({
  head: () =>
    buildHead({
      locale: "en",
      title: `${TITLE} · Shugert`,
      description: DESCRIPTION,
      path: PATH,
      altPath: alternatePath(PATH, "es"),
      ogImage: COVER_ABS,
      preloadImage: cover1200,
      preloadImageSrcset: COVER_SRCSET,
      preloadImageSizes: COVER_SIZES,
      jsonLd: [
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Shopify Marketing Strategy", path: PATH },
        ]),
        faqJsonLd(FAQS),
        serviceJsonLd({
          name: "Shopify Marketing Strategy",
          description:
            "Strategy, technical SEO, CRO, performance, and analytics for Shopify and Shopify Plus stores. We build the system that grows revenue without growing CAC.",
          path: PATH,
          serviceType: "Ecommerce Marketing Strategy",
          deliverables: [
            "Acquisition + conversion + retention strategy",
            "Technical SEO and site architecture",
            "Channel mix and budget allocation",
            "Analytics + attribution architecture",
            "90-day execution roadmap",
          ],
          locale: "en",
        }),
      ],
    }),
});
